import { ReactNode, useEffect, useState } from 'react';

import { i18n } from '@/i18n';
import { clearListState } from '@/lib/use-list-state';
import { authApi } from '@/services';

import { AuthContext, Role, User } from './auth-context';

export type { Role } from './auth-context';

/**
 * Roles allowed to sign into risp-admin. This app is staff-only, but the login endpoints
 * (`/auth/login`, `/auth/google`) are shared with the family website (risp-frontend), which
 * signs parents and students in through those exact same calls — so the API has no way to
 * enforce "staff only" itself. That check has to happen here, once, after the API has
 * already confirmed the credentials. Keep this the single source of truth for the list: a
 * second, drifted copy in `login` or `loginWithGoogle` is exactly how a parent or student
 * ends up back in the admin shell.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const ALLOWED_ROLES: Role[] = ['admin', 'teacher', 'employee'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    authApi
      .getMe()
      // The auth cookie is issued on AUTH_COOKIE_DOMAIN, so it reaches every subdomain: a
      // parent signed into the family portal arrives here already authenticated, without ever
      // touching the login form. Restoring a session has to clear the same bar as creating
      // one. Unlike the sign-in paths this does NOT call logout() — the cookie predates this
      // visit and belongs to their portal session, which we have no business ending.
      .then(restored => setUser(restored && ALLOWED_ROLES.includes(restored.role) ? restored : null))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  // Shared by both sign-in paths below: accept a staff role, or refuse and clean up.
  const applyStaffLogin = async (signedInUser: User) => {
    if (ALLOWED_ROLES.includes(signedInUser.role)) {
      setUser(signedInUser);
      return;
    }
    // The API has already accepted the credentials and set the auth cookie by this point.
    // Refusing in the UI while leaving that cookie live on the domain would only be a
    // half-fix, so clear it before surfacing the refusal.
    await authApi.logout().catch(() => {});
    throw new Error(i18n.t('auth.error_not_staff'));
  };

  const login = async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    // Session-expiry redirects to /login in the same tab without ever running logout()
    // (see api-client's 401 handler), so a shared machine can otherwise hand the next
    // sign-in the previous user's remembered filters. Clearing here closes that path too.
    clearListState();
    await applyStaffLogin(res.user);
  };

  const loginWithGoogle = async (idToken: string) => {
    const res = await authApi.loginWithGoogle(idToken);
    // Same reasoning as login() above: the expiry redirect never reaches logout(), so
    // sign-in is the reliable place to guarantee a clean slate on a shared machine.
    clearListState();
    await applyStaffLogin(res.user);
  };

  const logout = async () => {
    await authApi.logout().catch(() => {});
    setUser(null);
    // Shared school machines: the next person to sign in must not inherit these filters.
    clearListState();
    if (window.google?.accounts.id.disableAutoSelect) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, loginWithGoogle, logout, isLoading }}>{children}</AuthContext.Provider>
  );
}
